import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useIntl, IntlFormatters, FormattedMessage } from 'react-intl';
import DynamicTexts from '@components/common/DynamicTexts/DynamicTexts';
import Icon, { IconGlyph } from '@components/common/Icon';
import { IconSize } from '@constants/common';
import styles from './Loader.module.scss';

export enum LoaderType {
  FULL_PAGE = 'FULL_PAGE',
  PAGE = 'PAGE',
  CARD = 'CARD',
  BUTTON = 'BUTTON',
  BUTTON_S = 'BUTTON_S',
  EDITOR = 'EDITOR',
  PARAGRAPH = 'PARAGRAPH',
  NEW_TEXT = 'NEW_TEXT',
  DETECTOR = 'DETECTOR',
}

type LoaderConfig = {
  width: number;
  height: number;
  iconColor: string;
  iconName?: IconGlyph;
  modifier?: string;
  markup?: JSX.Element;
};

const getLoader = (
  loaderType: LoaderType,
  formatMessage: IntlFormatters['formatMessage']
): LoaderConfig => {
  const loaderEditorTitles = [
    formatMessage({ id: 'loader.editor_title_1' }),
    formatMessage({ id: 'loader.editor_title_2' }),
    formatMessage({ id: 'loader.editor_title_3' }),
    formatMessage({ id: 'loader.editor_title_4' }),
    formatMessage({ id: 'loader.editor_title_5' }),
    formatMessage({ id: 'loader.editor_title_6' }),
  ];

  switch (loaderType) {
    case LoaderType.FULL_PAGE: {
      const { width, height } = IconSize.LOADER_SIZE_M;
      return {
        width,
        height,
        modifier: 'full-page',
        iconColor: 'primary',
      };
    }
    case LoaderType.PAGE: {
      const { width, height } = IconSize.LOADER_SIZE_M;
      return {
        width,
        height,
        modifier: 'page',
        iconColor: 'primary',
      };
    }
    case LoaderType.EDITOR: {
      const { width, height } = IconSize.LOADER_SIZE_S;
      return {
        width,
        height,
        modifier: 'editor',
        iconColor: 'primary',
        iconName: IconGlyph.EDITOR_LOADER,
        markup: (
          <>
            <DynamicTexts
              texts={loaderEditorTitles}
              className='text-base fw-bold mt-xxxs c-main ff-secondary ta-center'
            />
            <p className='text-sm mt-xxxxxs c-main-lighter'>
              <FormattedMessage id='loader.editor_label' />
            </p>
          </>
        ),
      };
    }
    case LoaderType.CARD: {
      const { width, height } = IconSize.LOADER_SIZE_L;
      return {
        width,
        height,
        modifier: 'card',
        iconColor: 'primary',
      };
    }
    case LoaderType.BUTTON: {
      const { width, height } = IconSize.LOADER_SIZE_S;
      return {
        width,
        height,
        iconColor: 'white',
      };
    }
    case LoaderType.BUTTON_S: {
      const { width, height } = IconSize.LOADER_SIZE_XS;
      return {
        width,
        height,
        iconColor: 'inherit',
      };
    }
    case LoaderType.NEW_TEXT: {
      const { width, height } = IconSize.NEW_TEXT;
      return {
        width,
        height,
        iconColor: 'inherit',
      };
    }
    case LoaderType.DETECTOR: {
      const { width, height } = IconSize.LOADER_SIZE_XXS;
      return {
        width,
        height,
        iconColor: 'primary',
      };
    }
    default: {
      const { width, height } = IconSize.LOADER_SIZE_L;
      return {
        width,
        height,
        iconColor: 'white',
      };
    }
  }
};

type LoaderProps = {
  type: LoaderType;
  className?: string;
  width?: number;
  height?: number;
};

const Loader: React.FC<LoaderProps> = ({ type, className, width, height }) => {
  const { formatMessage } = useIntl();
  const {
    width: iconWidth,
    height: iconHeight,
    modifier,
    iconName = IconGlyph.LOADER,
    iconColor,
    markup = null,
  } = useMemo(() => getLoader(type, formatMessage), [type, formatMessage]);

  const resultWidth = width || iconWidth;
  const resultHeight = height || iconHeight;

  return (
    <div className={classNames(className, modifier && styles[modifier])}>
      <div
        className={classNames(styles.inner, `c-${iconColor}`)}
        style={{
          width: `${resultWidth}px`,
          height: `${resultHeight}px`,
        }}>
        <FormattedMessage id='loader.label' />
        <Icon
          glyph={iconName}
          size={resultWidth}
        />
      </div>
      {markup}
    </div>
  );
};

export default Loader;
